import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  User, ArrowLeft, History, Database, 
  Search, Calendar,
  CreditCard, Activity,
  Download, Eye, FileDown
} from 'lucide-react';
import LeadViewer from '../../components/ui/LeadViewer';
import { exportToCSV, exportToExcel, API_URL } from '../../services/api';

interface UserFullData {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    creditsRemaining: number;
    isAdmin: boolean;
    createdAt: string;
  };
  stats: {
    totalJobs: number;
    totalEmails: number;
  };
  jobs: Array<{
    id: string;
    target: string;
    scrapeType: string;
    scrapeGoal?: string;
    status: string;
    resultCount: number;
    createdAt: string;
    timestamp: string;
  }>;
}

export default function AdminUserView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<UserFullData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewingJobId, setViewingJobId] = useState<string | null>(null);
  const [exportType, setExportType] = useState<'idle' | 'csv' | 'excel'>('idle');


  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const res = await fetch(`${API_URL}/api/admin/users/${userId}/full-data`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const d = await res.json();
        if (d.success) {
          setData(d.data);
        } else {
          setError(d.message || 'Failed to load user data');
        }
      } catch (err) {
        setError('Network error loading user data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [userId, API_URL, token]);

  if (isLoading) return <div className="p-12 text-center text-gray-400">Syncing with user account...</div>;
  if (error || !data) return <div className="p-12 text-center text-red-500">{error || 'User not found'}</div>;

  const handleExport = async (job: any, type: 'csv' | 'excel') => {
    try {
      const res = await fetch(`${API_URL}/api/scrapes/${job.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const responseData = await res.json();
      const fullJob = responseData.data;
      
      if (!fullJob.results || fullJob.results.length === 0) {
        alert("No results found for this job.");
        return;
      }

      const flatResults = fullJob.results.map((r: any) => ({
        target: fullJob.target,
        username: r.username,
        email: r.email,
        verified: r.isVerified ? 'Yes' : 'No'
      }));

      if (type === 'csv') {
        exportToCSV(flatResults, `scrape_${fullJob.target}_${new Date().toISOString()}.csv`);
      } else {
        exportToExcel(flatResults, `scrape_${fullJob.target}_${new Date().toISOString()}.xlsx`);
      }
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data.');
    }
  };

  const handleExportAll = async (type: 'csv' | 'excel') => {
    try {
      const completedJobs = jobs.filter(j => j.status === 'completed');
      if (completedJobs.length === 0) {
        alert("No completed jobs to export.");
        return;
      }

      setExportType(type);
      
      const fullJobs = await Promise.all(
        completedJobs.map(job => 
          fetch(`${API_URL}/api/scrapes/${job.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(async res => {
            const resData = await res.json();
            return resData.data;
          })
        )
      );

      const allResults = fullJobs.flatMap(job => 
        (job.results || []).map((r: any) => ({
          job_target: job.target,
          job_type: job.scrapeType,
          job_date: new Date(job.createdAt).toLocaleDateString(),
          username: r.username,
          email: r.email,
          verified: r.isVerified ? 'Yes' : 'No'
        }))
      );

      if (allResults.length === 0) {
        alert("No results found in any of the user's jobs.");
        return;
      }

      if (type === 'csv') {
        exportToCSV(allResults, `all_leads_${user.firstName}_${new Date().toISOString()}.csv`);
      } else {
        exportToExcel(allResults, `all_leads_${user.firstName}_${new Date().toISOString()}.xlsx`);
      }
    } catch (err) {
      console.error('Bulk export failed:', err);
      alert('Failed to export bulk data.');
    } finally {
      setExportType('idle');
    }
  };

  const { user, stats, jobs } = data;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
           <button 
             onClick={() => navigate('/admin/users')}
             className="p-2 hover:bg-white/5 rounded-xl border border-white/5 text-gray-400 hover:text-white transition-all"
           >
             <ArrowLeft size={20} />
           </button>
           <div>
             <h1 className="text-3xl font-bold text-white font-outfit">User Profile</h1>
             <p className="text-gray-400 text-sm">Managing account of {user.firstName} {user.lastName}</p>
           </div>
        </div>
        
        <div className="flex items-center gap-3">
            <span className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border ${
                user.isAdmin 
                ? 'bg-purple-500/10 border-purple-500/20 text-purple-400' 
                : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
            }`}>
                {user.isAdmin ? 'Admin' : 'Normal User'}
            </span>
        </div>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatBox label="Balance" value={`${user.creditsRemaining} Credits`} icon={<CreditCard size={18}/>} color="purple" />
          <StatBox label="Total Runs" value={stats.totalJobs} icon={<Activity size={18}/>} color="blue" />
          <StatBox label="Total Leads" value={stats.totalEmails.toLocaleString()} icon={<Database size={18}/>} color="green" />
          <StatBox label="Joined" value={new Date(user.createdAt).toLocaleDateString()} icon={<Calendar size={18}/>} color="orange" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* User Details */}
        <div className="lg:col-span-4 space-y-6">
            <div className="bg-[#111] border border-white/10 rounded-[2rem] p-8 shadow-2xl">
                <h3 className="text-sm font-black text-gray-500 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
                    <User size={14} className="text-purple-400" /> Identity Info
                </h3>
                <div className="space-y-6">
                    <InfoField label="First Name" value={user.firstName} />
                    <InfoField label="Last Name" value={user.lastName} />
                    <InfoField label="Email Address" value={user.email} />
                    <InfoField label="Account ID" value={user.id} />
                </div>
                
                <div className="mt-10 p-5 rounded-2xl bg-white/5 border border-white/5 space-y-3">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Surveillance Summary</p>
                    <p className="text-xs text-gray-400 leading-relaxed italic">
                        "User is primarily focused on {jobs?.[0]?.scrapeType || 'none'} scrapes. 
                        Average yield per run is {stats.totalJobs > 0 ? Math.round(stats.totalEmails / stats.totalJobs) : 0} emails."
                    </p>
                </div>
            </div>
        </div>

        {/* Scrape History (User View Simulation) */}
        <div className="lg:col-span-8 space-y-6">
            <div className="bg-[#111] border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl">
                <div className="p-8 border-b border-white/10 flex justify-between items-center bg-white/5">
                    <h3 className="text-sm font-black text-gray-300 uppercase tracking-[0.2em] flex items-center gap-2">
                        <History size={16} className="text-purple-400" /> Historical Scrapes
                    </h3>
                    <div className="flex items-center gap-3">
                        {jobs.some(j => j.status === 'completed') && (
                          <>
                            <button 
                              onClick={() => handleExportAll('csv')}
                              disabled={exportType !== 'idle'}
                              className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-all text-gray-400 hover:text-white flex items-center gap-2 disabled:opacity-50"
                            >
                              <Download size={14} /> {exportType === 'csv' ? 'Processing...' : 'Bulk CSV'}
                            </button>
                            <button 
                              onClick={() => handleExportAll('excel')}
                              disabled={exportType !== 'idle'}
                              className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-all text-gray-400 hover:text-white flex items-center gap-2 disabled:opacity-50"
                            >
                              <FileDown size={14} /> {exportType === 'excel' ? 'Processing...' : 'Bulk XLSX'}
                            </button>
                          </>
                        )}
                        <span className="text-[10px] font-bold text-gray-500 uppercase px-3 py-1 bg-white/5 rounded-full border border-white/5">
                            {jobs.length} total entries
                        </span>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-[#0c0c0c]">
                            <tr>
                                <th className="px-8 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest">Target</th>
                                <th className="px-8 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest">Type</th>
                                <th className="px-8 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest">Yield</th>
                                <th className="px-8 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest">Status</th>
                                <th className="px-8 py-4 text-[10px] font-black text-gray-500 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {jobs.map((job) => (
                                <tr key={job.id} className="hover:bg-white/5 transition-all text-sm group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                                                <Search size={14} className="text-gray-400 group-hover:text-purple-400 transition-colors" />
                                            </div>
                                            <span className="font-bold text-gray-200">
                                                {(() => {
                                                    const hasHash = job.target.includes('#');
                                                    const text = job.target.replace(/@/g, '');
                                                    return hasHash ? text : `@${text}`;
                                                })()}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className="px-2.5 py-1 bg-white/5 rounded-lg border border-white/5 text-[10px] font-bold text-gray-400 uppercase">
                                            {job.target.includes('#') ? 'Hashtag Search' : job.scrapeType}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex flex-col">
                                            <span className="text-gray-300 font-bold tracking-tight">
                                                {job.resultCount} {job.scrapeGoal === 'profiles' ? 'profiles' : (job.scrapeGoal === 'list' ? 'users' : 'emails')}
                                            </span>
                                            <span className="text-[9px] text-gray-600 font-black uppercase">{new Date(job.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-1.5 h-1.5 rounded-full ${job.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                            <span className="font-bold text-gray-400 capitalize">{job.status}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center justify-end gap-2">
                                            {(job.status === 'completed' || ((job.status === 'canceled' || job.status === 'failed') && (job.resultCount ?? 0) > 0)) && (
                                              <>
                                                <button 
                                                  onClick={() => setViewingJobId(job.id)}
                                                  className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all"
                                                  title="View Leads"
                                                >
                                                  <Eye size={16} />
                                                </button>
                                                <button 
                                                  onClick={() => handleExport(job, 'csv')}
                                                  className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all"
                                                  title="Export CSV"
                                                >
                                                  <Download size={16} />
                                                </button>
                                                <button 
                                                  onClick={() => handleExport(job, 'excel')}
                                                  className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-all"
                                                  title="Export Excel"
                                                >
                                                  <FileDown size={16} />
                                                </button>
                                              </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {jobs.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-8 py-12 text-center text-gray-600 text-sm font-bold">
                                        No historical data available for this user.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
      </div>
      
      <LeadViewer 
        jobId={viewingJobId} 
        onClose={() => setViewingJobId(null)} 
      />
    </div>
  );
}

function StatBox({ label, value, icon, color }: any) {
    const colors: any = {
        purple: 'from-purple-500/20 border-purple-500/20 text-purple-400',
        blue: 'from-blue-500/20 border-blue-500/20 text-blue-400',
        green: 'from-emerald-500/20 border-emerald-500/20 text-emerald-400',
        orange: 'from-orange-500/20 border-orange-500/20 text-orange-400',
    };
    return (
        <div className={`bg-gradient-to-br bg-[#111] ${colors[color]} border rounded-3xl p-6 shadow-xl`}>
            <div className="flex justify-between items-center mb-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</span>
                <div className="p-2 bg-black/40 rounded-xl">{icon}</div>
            </div>
            <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
        </div>
    );
}

function InfoField({ label, value }: { label: string, value: string }) {
    return (
        <div className="space-y-1.5">
            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{label}</label>
            <div className="bg-white/5 border border-white/5 rounded-xl px-4 py-3 text-sm text-gray-200 font-semibold truncate">
                {value}
            </div>
        </div>
    );
}
